import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { enqueueCartMutation, cartNeedsRestoration, cartRestoringEvent } from '@theme/cart-synchronization';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;
  #updating = false;
  #quantityIntent;
  /** @type {WeakMap<CartLinesUpdateEvent, Promise<void> | undefined>} */
  #cartRenders = new WeakMap();

  /**
   * Let Horizon's drawer await this event's existing render before opening.
   * @param {CartLinesUpdateEvent} event
   */
  whenCartRendered(event) {
    if (!this.#cartRenders.has(event)) this.#handleCartUpdate(event);
    return this.#cartRenders.get(event);
  }

  #handleQuantityUpdate = (event) => {
    // Disabling a cached focused input can fire blur/setQuantity. Do not let that
    // draft become a delayed POST after the restoration has already completed.
    if (cartNeedsRestoration()) {
      this.#debouncedOnChange.cancel();
      this.#quantityIntent = undefined;
      return;
    }
    if (!(event instanceof QuantitySelectorUpdateEvent) || !(event.target instanceof Node) || !this.contains(event.target) || this.#updating) return;
    const row = this.refs.cartItemRows?.[event.detail.cartLine - 1];
    if (!row) return;
    this.#quantityIntent = { key: row.dataset.key, identity: row.dataset.cartLineIdentity, quantity: event.detail.quantity };
    this.#debouncedOnChange(event);
  };

  #findCartLine(key, identity) {
    const rows = this.refs.cartItemRows ?? [];
    const exact = rows.findIndex(row => row.dataset.key === key);
    if (exact >= 0) return exact;
    const matching = identity ? rows.filter(row => row.dataset.cartLineIdentity === identity) : [];
    return matching.length === 1 ? rows.indexOf(matching[0]) : -1;
  }

  updatedCallback() {
    super.updatedCallback();
    // Preserve committed-but-debounced intent if a discount refresh lands between taps.
    // Totals and row metadata remain server-confirmed; only the editable draft is retained.
    const intent = this.#quantityIntent;
    if (!intent || this.#updating || cartNeedsRestoration()) return;
    const row = this.refs.cartItemRows?.[this.#findCartLine(intent.key, intent.identity)];
    const input = row?.querySelector('.quantity-selector input');
    if (input instanceof HTMLInputElement && input !== document.activeElement) input.value = String(intent.quantity);
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(async ({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
        this.updatedCallback();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(async ({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          await morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
        this.updatedCallback();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    document.addEventListener(cartRestoringEvent, this.#handleQuantityUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
    document.removeEventListener(cartRestoringEvent, this.#handleQuantityUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    this.#debouncedOnChange.cancel();
    this.#quantityIntent = undefined;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const intent = this.#quantityIntent;
    this.#quantityIntent = undefined;
    if (!intent || this.#updating || cartNeedsRestoration()) return;
    const currentLine = this.#findCartLine(intent.key, intent.identity);
    if (currentLine < 0) {
      this.#handleCartError(event.detail.cartLine, { errors: 'This cart item changed. Please check your cart before trying again.' }, intent.key);
      return;
    }
    const quantity = intent.quantity;
    const line = currentLine + 1;
    if (quantity === Number(this.refs.cartItemRows?.[line - 1]?.dataset.confirmedQuantity)) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.#debouncedOnChange.cancel();
    this.#quantityIntent = undefined;
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    // Keep the line (and its error region) until Shopify confirms the removal.
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (this.#updating || !this.isConnected || cartNeedsRestoration()) return;
    this.#updating = true;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const row = this.refs.cartItemRows?.[line - 1];
    const lineId = row?.dataset.key ?? '';
    const lineIdentity = row?.dataset.cartLineIdentity;
    row?.querySelector('.cart-items__error')?.classList.add('hidden');
    this.querySelector('[data-cart-update-error]')?.remove();
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    cartTotal?.shimmer();

    let deferredUpdatePromise;
    return enqueueCartMutation(async () => {
      if (!this.isConnected) return;
      this.#disableCartItems();
      // A preceding discount refresh may have morphed/reordered rows. Keep the original
      // line identity; never send a queued positional edit to a different product.
      const currentLine = this.#findCartLine(lineId, lineIdentity);
      if (currentLine < 0) throw new Error('Cart line changed before the update');
      const body = JSON.stringify({
        line: currentLine + 1,
        quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });
      deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
          context: 'cart',
          lines: [{ id: lineId, quantity }],
          promise: deferredUpdatePromise.promise,
        })
      );

      return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
        .then(async (response) => {
          const parsedResponseText = await response.json();

          resetShimmer(this);

          if (!response.ok || parsedResponseText.errors) {
            const message = parsedResponseText.errors || parsedResponseText.description || parsedResponseText.message || 'The cart could not be updated.';
            this.#handleCartError(line, { errors: message }, lineId);
            deferredUpdatePromise.reject(new Error(message));
            return;
          }

          const sectionHTML = parsedResponseText.sections?.[this.sectionId];
          const render = async () => {
            if (sectionHTML) {
              await morphSection(this.sectionId, sectionHTML, { mode: this.isDrawer ? 'hydration' : 'full' });
            } else {
              await sectionRenderer.renderSection(this.sectionId, { cache: false, mode: this.isDrawer ? 'hydration' : 'full' });
            }

            this.#updateQuantitySelectors(parsedResponseText);
            deferredUpdatePromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
              detail: {
                sections: parsedResponseText.sections,
                items: parsedResponseText.items,
                itemCount: parsedResponseText.item_count,
                source: 'cart-items-component',
                didError: false,
              },
            });
            this.#updateCartQuantitySelectorButtonStates();
          };
          // Use the existing empty-cart transition only after a confirmed mutation.
          if (quantity === 0 && parsedResponseText.item_count === 0) {
            await startViewTransition(render, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);
          } else {
            await render();
          }
        });
    })
      .catch((error) => {
        console.error(error);
        this.#handleCartError(line, {
          errors: 'We could not confirm your cart update. Your last confirmed quantities are shown. Please refresh the cart before trying again.',
        }, lineId, 'SERVICE_UNAVAILABLE');
        deferredUpdatePromise?.reject(error);
      })
      .finally(() => {
        resetShimmer(this);
        this.#debouncedOnChange.cancel();
        this.#updating = false;
        this.#enableCartItems();
        // Server row metadata remains current even while a focused input preserves its draft.
        for (const row of this.refs.cartItemRows ?? []) {
          const input = row.querySelector('.quantity-selector input');
          if (input instanceof HTMLInputElement) input.value = row.dataset.confirmedQuantity ?? input.defaultValue;
        }
        this.#updateCartQuantitySelectorButtonStates();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   * @param {string} [lineId] - Stable line key, including properties/selling-plan identity.
   * @param {string} [code] - Standardized cart error code.
   */
  #handleCartError = (line, parsedResponseText, lineId, code = 'INVALID') => {
    const row = lineId
      ? this.refs.cartItemRows?.find((item) => item.dataset.key === lineId)
      : this.refs.cartItemRows?.[line - 1];
    const quantityInput = row?.querySelector('.quantity-selector input');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = row.dataset.confirmedQuantity ?? quantityInput.defaultValue;
    }

    const cartItemError = row?.querySelector('.cart-item__error-text');
    const cartItemErrorContainer = row?.querySelector('.cart-items__error');
    if (cartItemError && cartItemErrorContainer) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    } else {
      // A concurrent server refresh may have removed the line; still announce the failure.
      const feedback = document.createElement('p');
      feedback.dataset.cartUpdateError = '';
      feedback.className = 'cart-item__error-text cart-primary-typography';
      feedback.setAttribute('role', 'alert');
      feedback.textContent = parsedResponseText.errors;
      this.append(feedback);
    }

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code,
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    const rendered = event.promise
      ?.then(async ({ detail }) => {
        if (detail?.renderedSections?.includes(this.sectionId)) {
          this.#updateCartQuantitySelectorButtonStates();
          return;
        }
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer && this.closest('theme-drawer')?.hasAttribute('open')) {
            await startViewTransition(() => morphSection(this.sectionId, cartItemsHtml, morphOptions), ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          this.updatedCallback();
          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else if (!detail?.failedSections?.includes(this.sectionId)) {
          await sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      });
    this.#cartRenders.set(event, rendered);
    rendered?.catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
    });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
